/**
 * Public Audioguía player — works for ANY tour with a published guide.
 *
 * `?lang=` selects the narration language, `?code=` (optional) links the
 * session to a booking so downloads and analytics are attributed. No prices,
 * no checkout, no AI at runtime.
 */
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Headphones, Clock, Globe2, MapPin } from "lucide-react";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { AudioGuidePlayer } from "@/components/audioguide/AudioGuidePlayer";
import { PhotoUpload } from "@/components/audioguide/PhotoUpload";
import { getAudioGuide, trackAudioGuideEvent } from "@/lib/audio-guides.functions";
import { guideStrings } from "@/lib/audio-guide-i18n";
import {
  formatDuration,
  LANG_LABELS,
  type AudioGuideLang,
} from "@/lib/audio-guide-templates";

const SearchSchema = z.object({
  lang: z.enum(["es", "en", "fr", "it", "de", "pt"]).optional(),
  code: z.string().trim().max(16).optional(),
});

export const Route = createFileRoute("/audioguia/$tourSlug")({
  validateSearch: (search: Record<string, unknown>) => SearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: async ({ params, deps }) => {
    const res = await getAudioGuide({
      data: { tourSlug: params.tourSlug, ...(deps.lang ? { lang: deps.lang } : {}) },
    });
    if (!res.guide) throw notFound();
    return res.guide;
  },
  head: ({ loaderData }) => {
    const title = loaderData
      ? `Audioguía · ${loaderData.tourTitle} | Mostrando México`
      : "Audioguía | Mostrando México";
    const description = loaderData
      ? `Escucha la audioguía de ${loaderData.tourTitle}${loaderData.destination ? ` en ${loaderData.destination}` : ""}: ${loaderData.chapters.length} capítulos narrados, disponibles en ${loaderData.langsAvailable.length} idiomas.`
      : "Audioguías narradas de Mostrando México.";
    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ];
    if (loaderData?.coverImageUrl?.startsWith("https://")) {
      meta.push({ property: "og:image", content: loaderData.coverImageUrl });
      meta.push({ name: "twitter:image", content: loaderData.coverImageUrl });
    }
    return { meta };
  },
  errorComponent: () => (
    <Shell>
      <p className="text-sm text-muted-foreground">
        No pudimos cargar esta audioguía. Intenta de nuevo en un momento.
      </p>
    </Shell>
  ),
  notFoundComponent: () => (
    <Shell>
      <h1 className="text-xl font-bold text-foreground">Audioguía no disponible</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Esta experiencia todavía no tiene audioguía publicada.
      </p>
    </Shell>
  ),
  component: AudioGuidePage,
});

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-16">
      <div className="rounded-3xl border border-border bg-card p-8 text-center">{children}</div>
    </main>
  );
}

function AudioGuidePage() {
  const guide = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const strings = useMemo(() => guideStrings(guide.lang), [guide.lang]);
  const [started, setStarted] = useState(false);
  const code = search.code ?? null;

  useEffect(() => {
    void trackAudioGuideEvent({
      data: {
        eventType: "guide_opened",
        tourSlug: guide.tourSlug,
        guideId: guide.id,
        lang: guide.lang,
        ...(code ? { accessCode: code } : {}),
      },
    }).catch(() => undefined);
  }, [guide.id, guide.lang, guide.tourSlug, code]);

  const track = (
    eventType: "chapter_played" | "chapter_completed" | "guide_completed",
    chapterKey?: string | null,
  ) => {
    void trackAudioGuideEvent({
      data: {
        eventType,
        tourSlug: guide.tourSlug,
        guideId: guide.id,
        lang: guide.lang,
        ...(chapterKey ? { chapterKey } : {}),
        ...(code ? { accessCode: code } : {}),
      },
    }).catch(() => undefined);
  };

  return (
    <main className="mx-auto w-full max-w-3xl px-4 pb-24 pt-6 sm:pt-10">
      {/* Cover */}
      <section className="relative overflow-hidden rounded-3xl border border-border bg-card">
        {guide.coverImageUrl && (
          <img
            src={guide.coverImageUrl}
            alt={guide.tourTitle}
            className="h-48 w-full object-cover sm:h-72"
            loading="eager"
            fetchPriority="high"
          />
        )}
        <div className="p-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary">
            {strings.brand} · {strings.kicker}
          </p>
          <h1 className="mt-2 text-2xl font-black leading-tight text-foreground sm:text-3xl">
            {guide.tourTitle}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">{strings.coverTagline}</p>

          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
            {guide.destination && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" /> {guide.destination}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <Headphones className="h-3.5 w-3.5" /> {guide.chapters.length} {strings.chapters}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> {formatDuration(guide.totalDurationSeconds)}
            </span>
          </div>

          {guide.langsAvailable.length > 1 && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Globe2 className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="sr-only">{strings.language}</span>
              {guide.langsAvailable.map((l: AudioGuideLang) => (
                <Button
                  key={l}
                  size="sm"
                  variant={l === guide.lang ? "default" : "outline"}
                  onClick={() =>
                    navigate({ search: (prev) => ({ ...prev, lang: l }), replace: true })
                  }
                >
                  {LANG_LABELS[l]}
                </Button>
              ))}
            </div>
          )}

          {!started && (
            <Button size="lg" className="mt-6 w-full sm:w-auto" onClick={() => setStarted(true)}>
              <Headphones className="mr-2 h-5 w-5" />
              {strings.start}
            </Button>
          )}
        </div>
      </section>

      {/* Player */}
      {started && (
        <div className="mt-8">
          <AudioGuidePlayer
            tourSlug={guide.tourSlug}
            lang={guide.lang}
            chapters={guide.chapters.map((c) => ({
              id: c.id,
              position: c.position,
              key: c.key,
              title: c.title,
              durationSeconds: c.durationSeconds,
            }))}
            strings={strings}
            accessCode={code}
            onChapterPlayed={(c) => track("chapter_played", c.key)}
            onChapterCompleted={(c) => track("chapter_completed", c.key)}
            onGuideCompleted={() => track("guide_completed")}
          />
        </div>
      )}

      {/* Traveler photos */}
      <div className="mt-10">
        <PhotoUpload
          tourSlug={guide.tourSlug}
          lang={guide.lang}
          accessCode={code}
          strings={strings}
        />
      </div>

      <p className="mt-8 text-center text-xs text-muted-foreground">
        <Link to="/" className="underline-offset-4 hover:underline">
          Mostrando México
        </Link>
      </p>
    </main>
  );
}
